'use client'

import PropTypes from 'prop-types'
import { TextEntry } from '@repo/ui/TextEntry'
import { useClientStore } from '@repo/store/useClientStore'
import { useLookupsStore } from '@repo/store/useLookupsStore'
import { useEffect, useState } from 'react'
import { useDebounce } from '@uidotdev/usehooks'

export const NewClientNumberEntry = ({
  onChange,
  onError,
  surname,
  size = 'small',
  sx = {},
  testMode = false,
  defaultValue = ''
}) => {
  const { fetchNextClientNo, checkIfClientNoExists } = useClientStore()
  const { fetchClientAutonumbering, clientAutonumbering } = useLookupsStore()
  const [clientNo, setClientNo] = useState(defaultValue)
  const [autoNumberingType, setAutoNumberingType] = useState(null)
  const [error, setError] = useState(null)
  const debouncedSurname = useDebounce(surname, 300)
  const debouncedClientNo = useDebounce(clientNo, 300)

  useEffect(() => {
    if (!clientAutonumbering && !testMode) {
      fetchClientAutonumbering()
    }
  }, [clientAutonumbering, fetchClientAutonumbering, testMode])

  useEffect(() => {
    if (!clientAutonumbering) return
    setAutoNumberingType(clientAutonumbering.autoNumberingType || null)
  }, [clientAutonumbering])

  useEffect(() => {
    if (!autoNumberingType || autoNumberingType === 'Off') return

    if (!debouncedSurname) {
      setClientNo('')
      return
    }

    const getNextClientNo = async () => {
      const clientNo = await fetchNextClientNo({ surname: debouncedSurname })
      setClientNo(clientNo)
    }

    if (!testMode) getNextClientNo()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSurname, autoNumberingType])

  useEffect(() => {
    if (!autoNumberingType || autoNumberingType !== 'Off' || !debouncedClientNo) return

    const checkIfClientNoIsUnique = async () => {
      const exists = await checkIfClientNoExists({ clientNo: debouncedClientNo })
      const errorMessage = exists ? 'Client no already exists' : null
      setError(errorMessage)
      if (onError) onError(errorMessage)
    }

    checkIfClientNoIsUnique()
  }, [debouncedClientNo, autoNumberingType, checkIfClientNoExists, onError])

  const handleChange = (newValue) => {
    if (autoNumberingType === 'Off') {
      // 1. Remove any newline characters so input stays on a single line
      const singleLine = newValue.replace(/[\r\n]/g, '')

      // remove ! # ' $ % space ( ) : ; ^ , @ / | \ & . [ ] { } _
      // eslint-disable-next-line
      const sanitized = singleLine.replace(/[!#'\$%\s():;\^,@\/|\\&\.\[\]\{\}_¬"£\*=~?><\+=]/g, '')

      setClientNo(sanitized)
    }
  }

  useEffect(() => {
    if (onChange) {
      onChange(clientNo)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientNo])

  const getHelperText = () => {
    if (autoNumberingType === 'Off') {
      return 'Please enter a unique client number'
    }

    return 'Automatically generated based on name'
  }

  return (
    <div data-testid="NewClientNumberEntry">
      <TextEntry
        name="clientNumber"
        onChange={handleChange}
        value={clientNo || ''}
        disabled={autoNumberingType !== 'Off' && !surname}
        required
        limit={10}
        label="Client no"
        readOnly={autoNumberingType !== 'Off'}
        errors={error ? { clientNumber: error } : null}
        size={size}
        helperText={getHelperText()}
        sx={{ ...sx }}
      />
    </div>
  )
}

NewClientNumberEntry.propTypes = {
  onChange: PropTypes.func,
  onError: PropTypes.func,
  surname: PropTypes.string,
  size: PropTypes.string,
  sx: PropTypes.object,
  testMode: PropTypes.bool,
  defaultValue: PropTypes.string
}
